import type { CardEngineInput } from "@tycho/web-bindings/types";
import type { SmbActionDefinition } from "@/web/routes/_protected/smb/-dashboard/-types";

/**
 * Engine pattern: rule arrays + named predicates + first-class fan-out.
 *
 * Two arrays of rules are passed to the engine:
 *   - `uncompleted`: cards prompting the user to do something. Sorted by
 *     `priority` in the output.
 *   - `completed`: cards reflecting things the user has already done. No
 *     priority; engine emits in declaration order.
 *
 * Each rule has `forEach` (what to iterate over), `when` (per-item filter),
 * and `build` (per-item card). Singleton rules omit `forEach` and the engine
 * runs them once. Per-property/per-question rules just set `forEach` to the
 * right collection.
 *
 * Predicates are typed by item context: `Predicate<Property>` is a per-
 * property check, `Predicate` (default `void`) is a data-level check. They
 * compose freely with `and`/`or`/`not` thanks to TS parameter contravariance —
 * a `Predicate<void>` is assignable wherever a `Predicate<Property>` is
 * expected.
 *
 * Co-location: each rule object is a value. Action files export their own
 * `<name>Rule` and (optionally) `<name>CompletedRule`; the engine module
 * just collects them into the two registry arrays.
 */

type Data = CardEngineInput;
export type UWQuestion = Data["underwritingResults"][number]["questions"][number];
export type UnderwritingResult = Data["underwritingResults"][number];
export type Property = Data["properties"][number];

type Predicate<T = void> = (d: Data, ...rest: [T] extends [void] ? [] : [item: T]) => boolean;
type ForEachSelector<T> = (d: Data) => T[];

type SingletonUncompletedRule = {
  id: string;
  priority: number | ((d: Data) => number);
  when: (d: Data) => boolean;
  build: (d: Data) => SmbActionDefinition;
};

type ForEachUncompletedRule<T> = {
  id: string;
  priority: number | ((d: Data, item: T) => number);
  forEach: ForEachSelector<T>;
  when: (d: Data, item: T) => boolean;
  build: (d: Data, item: T) => SmbActionDefinition;
};

type SingletonCompletedRule = {
  id: string;
  when: (d: Data) => boolean;
  build: (d: Data) => SmbActionDefinition;
};

type ForEachCompletedRule<T> = {
  id: string;
  forEach: ForEachSelector<T>;
  when: (d: Data, item: T) => boolean;
  build: (d: Data, item: T) => SmbActionDefinition;
};

// `any` at the existential boundary — `ForEachRule<T>` is contravariant in T,
// so `ForEachRule<Property>` is not assignable to `ForEachRule<unknown>`. We
// only need to call the rule's own callbacks with the rule's own item type,
// which is preserved at construction time inside the rule object.
// oxlint-disable-next-line typescript/no-explicit-any
type UncompletedRule = SingletonUncompletedRule | ForEachUncompletedRule<any>;
// oxlint-disable-next-line typescript/no-explicit-any
type CompletedRule = SingletonCompletedRule | ForEachCompletedRule<any>;

// Builders — identity functions that exist for type inference on rule objects.
const singletonRule = (rule: SingletonUncompletedRule): SingletonUncompletedRule => rule;
const forEachRule = <T>(rule: ForEachUncompletedRule<T>): ForEachUncompletedRule<T> => rule;
const singletonCompletedRule = (rule: SingletonCompletedRule): SingletonCompletedRule => rule;
const forEachCompletedRule = <T>(rule: ForEachCompletedRule<T>): ForEachCompletedRule<T> => rule;

// Predicate combinators.
const and =
  <T = void>(...ps: Predicate<T>[]): Predicate<T> =>
  (d, ...rest) =>
    ps.every((p) => p(d, ...rest));
const or =
  <T = void>(...ps: Predicate<T>[]): Predicate<T> =>
  (d, ...rest) =>
    ps.some((p) => p(d, ...rest));
const not =
  <T = void>(p: Predicate<T>): Predicate<T> =>
  (d, ...rest) =>
    !p(d, ...rest);

// Data-level predicates.
const always: Predicate = () => true;
const hasNoProperty: Predicate = (d) => d.properties.length === 0;

/**
 * Business-story predicate. `businessStoryIs("Organic")` / `businessStoryIs("Invited")`
 * is the single reusable check for a brand's persistent origin/workflow
 * classification. The story is scoped per `card_engine_input` (resolved from the
 * owning brand server-side), so it is safe to compose into any card rule without
 * leaking one brand's policy into another deck.
 *
 * Prefer negating the explicit `Invited` check (`not(businessStoryIs("Invited"))`)
 * over an `Organic` allowlist — a future story must not inherit hidden cards by
 * accident.
 */
const businessStoryIs =
  (tag: "Organic" | "Invited"): Predicate =>
  (d) =>
    d.businessStory.tag === tag;

// Operating-locations predicates. The "confirm operating locations" card shows
// until the user has confirmed at least one location for the brand. The card
// itself renders loading / error states from the job status; the engine only
// needs the confirm gate here.
const hasNoConfirmedOperatingLocation: Predicate = (d) => d.operatingLocations.confirmedCount === 0;
// The "confirm operating locations" step is resolved once the user has either
// confirmed at least one location, or the discovery job terminally found
// nothing to confirm (no-results / failed / timed-out / never ran). Gates the
// ExploreProperties card: we don't want to invite the user to pick target
// properties until they've passed through (or had nothing to do at) the confirm
// step. While discovery is still pending — or completed with unconfirmed rows
// the user could still act on — this stays false so Explore holds back.
const operatingLocationsResolved: Predicate = (d) => {
  if (d.operatingLocations.confirmedCount > 0) return true;
  const status = d.operatingLocations.jobStatus?.tag;
  return (
    status === undefined || status === "NoResults" || status === "Failed" || status === "TimedOut"
  );
};
const hasProperty: Predicate = (d) => d.properties.length > 0;
const hasNoShareableBusinessCard: Predicate = (d) => d.shareableBusinessCards.length === 0;
// The semantic gate for the "compose your investor page" action: the brand has
// a shareable business card to attach a page to. Independent of onboarding
// event ordering — if a card exists, composing a TTW page is allowed.
const hasShareableBusinessCard: Predicate = (d) => d.shareableBusinessCards.length > 0;
// True once any of the brand's cards has a saved composer (TTW) page. Existence
// is denormalized onto each card row by the `card_engine_inputs` view.
const hasComposerPage: Predicate = (d) => d.shareableBusinessCards.some((c) => c.hasComposerPage);
// True once any of the brand's cards has a saved classical (non-LLM) TTW page.
// Denormalized onto each card row by the `card_engine_inputs` view, alongside
// `hasComposerPage`.
const hasTtwPage: Predicate = (d) => d.shareableBusinessCards.some((c) => c.hasTtwPage);
const hasConnection =
  (kind: keyof Data["financialConnections"]): Predicate =>
  (d) =>
    d.financialConnections[kind].length > 0;
const missingConnection =
  (kind: keyof Data["financialConnections"]): Predicate =>
  (d) =>
    d.financialConnections[kind].length === 0;

// Property-level predicates.
const propertyHasUnreviewedComps: Predicate<Property> = (_, p) =>
  p.comps.length > 0 && (p.selectedCompIds?.length ?? 0) === 0;
const propertyHasReviewedComps: Predicate<Property> = (_, p) =>
  (p.selectedCompIds?.length ?? 0) > 0;
const propertyCompsPending: Predicate<Property> = (_, p) =>
  p.comps.length === 0 && (p.compsJobStatus == null || p.compsJobStatus.tag === "Pending");
const propertyCompsUnavailable: Predicate<Property> = (_, p) =>
  p.comps.length === 0 &&
  (p.compsJobStatus?.tag === "Failed" ||
    p.compsJobStatus?.tag === "NoResults" ||
    p.compsJobStatus?.tag === "TimedOut");

// Underwriting-results job predicates. The job lives on the singleton
// `data.underwritingResults[0]`; absence of a status means the job hasn't
// been observed yet, which we treat as pending.
const underwritingResultsPending: Predicate = (d) => {
  const ur = d.underwritingResults[0];
  return (
    ur != null &&
    (ur.underwritingResultsJobStatus == null || ur.underwritingResultsJobStatus.tag === "Pending")
  );
};
const underwritingResultsUnavailable: Predicate = (d) => {
  const ur = d.underwritingResults[0];
  return (
    ur != null &&
    (ur.underwritingResultsJobStatus?.tag === "Failed" ||
      ur.underwritingResultsJobStatus?.tag === "TimedOut")
  );
};
const hasMaxAffordability: Predicate = (d) => {
  const sorted = d.underwritingResults.toSorted(
    (a, b) => new Date(b.runDate ?? 0).getTime() - new Date(a.runDate ?? 0).getTime(),
  );
  const ur = sorted[0];
  return ur != null && ur.maxAffordability !== undefined && ur.maxAffordability !== null;
};
const propertyHasNoMaxPrice: Predicate<Property> = (_, p) => p.userMaxPrice === undefined;
const propertyHasMaxPrice: Predicate<Property> = (_, p) => p.userMaxPrice !== undefined;
const propertyHasNoLandlordInvited: Predicate<Property> = (_, p) => !p.invitedLandlord;
const propertyHasLandlordInvited: Predicate<Property> = (_, p) => !!p.invitedLandlord;

// Question-level predicates.
const isUnansweredQuestion: Predicate<UWQuestion> = (_, q) => q.value.answer === undefined;
const isAnsweredQuestion: Predicate<UWQuestion> = (_, q) => q.value.answer !== undefined;

// Scheduled call predicates.
const hasNoScheduledCalls: Predicate = (d) => d.scheduledCalls.length === 0;
// True when Athena has reported the brand has finished its initial question
// set. Flag lives on the underwriting entity (one entity per brand), so
// every entry of `d.underwritingResults` carries the same value; reading
// from the latest run is enough to gate the "schedule a call" action.
const hasInitialQuestionsComplete: Predicate = (d) => {
  const latest = d.underwritingResults.toSorted(
    (a, b) => new Date(b.runDate ?? 0).getTime() - new Date(a.runDate ?? 0).getTime(),
  )[0];
  return latest?.initialQuestionsComplete === true;
};

// forEach selectors.
const property: ForEachSelector<Property> = (d) => d.properties;
const latestQuestion: ForEachSelector<UWQuestion> = (d) => {
  // we don't need to worry about timezone bullshit because only relative order matters
  const sorted = [...d.underwritingResults].sort(
    (a, b) => new Date(b.runDate ?? 0).getTime() - new Date(a.runDate ?? 0).getTime(),
  );
  return sorted[0]?.questions ?? [];
};

// True when the latest underwriting run has no unanswered questions, or when
// no run exists yet at all. Signals "we're computing a run" — either the very
// first one, or a follow-up after the user answered the previous batch.
const hasNoUnansweredQuestions: Predicate = (d) => {
  const latest = latestQuestion(d);
  return latest.every((q) => q.value.answer !== undefined);
};

const quickbooksNeedsMoreInfoQuestions: ForEachSelector<UWQuestion> = (d) =>
  latestQuestion(d).filter(
    (q) => q.tag === "NeedsMoreInfo" && q.value.infoType.tag === "Quickbooks",
  );

const quickbooksYesAnswerCount = (d: Data): number =>
  quickbooksNeedsMoreInfoQuestions(d).filter((q) => q.value.answer?.value === true).length;

// Each "yes" on a Quickbooks NeedsMoreInfo question is a commitment to connect
// one accounting item. Show the connect card until connection count catches up.
const needsMoreQuickbooksConnections: Predicate = (d) => {
  const requiredConnections = quickbooksYesAnswerCount(d) + 1;
  // One QuickBooks connection is required by the initial ask, plus one for each
  // later "yes" answer to a QuickBooks NeedsMoreInfo question.
  return d.financialConnections.accounting.length < requiredConnections;
};

// Per-connection forEach builder. The completed counterpart of
// `missingConnection(kind)`: emits one item per existing connection so we can
// render one "Banking connected" card per linked account.
const eachConnection = <K extends keyof Data["financialConnections"]>(
  kind: K,
): ForEachSelector<Data["financialConnections"][K][number]> => {
  return (d) => d.financialConnections[kind];
};

// Engine — runs a set of uncompleted + completed rules and returns two lists.
const run = (
  data: Data,
  rules: { uncompleted: readonly UncompletedRule[]; completed: readonly CompletedRule[] },
): { uncompleted: SmbActionDefinition[]; completed: SmbActionDefinition[] } => {
  type Emitted = { priority: number; def: SmbActionDefinition };
  const uncompletedOut: Emitted[] = [];
  const completedOut: SmbActionDefinition[] = [];

  for (const r of rules.uncompleted) {
    if ("forEach" in r) {
      for (const item of r.forEach(data)) {
        if (!r.when(data, item)) continue;
        const priority = typeof r.priority === "function" ? r.priority(data, item) : r.priority;
        uncompletedOut.push({ priority, def: r.build(data, item) });
      }
    } else {
      if (!r.when(data)) continue;
      const priority = typeof r.priority === "function" ? r.priority(data) : r.priority;
      uncompletedOut.push({ priority, def: r.build(data) });
    }
  }

  for (const r of rules.completed) {
    if ("forEach" in r) {
      for (const item of r.forEach(data)) {
        if (!r.when(data, item)) continue;
        completedOut.push(r.build(data, item));
      }
    } else {
      if (!r.when(data)) continue;
      completedOut.push(r.build(data));
    }
  }

  return {
    uncompleted: uncompletedOut.sort((a, b) => b.priority - a.priority).map((e) => e.def),
    completed: completedOut,
  };
};

/**
 * Single namespaced export so action files can import one thing and pull
 * helpers off it (e.g. `CardRulesEngine.forEachRule`, `CardRulesEngine.property`).
 */
export const CardRulesEngine = {
  // Rule builders.
  singletonRule,
  forEachRule,
  singletonCompletedRule,
  forEachCompletedRule,

  // Combinators.
  and,
  or,
  not,

  // Data-level predicates.
  always,
  hasNoProperty,
  businessStoryIs,
  hasNoConfirmedOperatingLocation,
  operatingLocationsResolved,
  hasProperty,
  hasNoShareableBusinessCard,
  hasShareableBusinessCard,
  hasComposerPage,
  hasTtwPage,
  hasConnection,
  missingConnection,

  // Item-level predicates.
  propertyHasUnreviewedComps,
  propertyHasReviewedComps,
  propertyCompsPending,
  propertyCompsUnavailable,
  propertyHasNoMaxPrice,
  propertyHasMaxPrice,
  propertyHasNoLandlordInvited,
  propertyHasLandlordInvited,
  isUnansweredQuestion,
  isAnsweredQuestion,
  hasNoUnansweredQuestions,
  underwritingResultsPending,
  underwritingResultsUnavailable,
  hasMaxAffordability,
  hasNoScheduledCalls,
  hasInitialQuestionsComplete,
  needsMoreQuickbooksConnections,

  // forEach selectors.
  property,
  latestQuestion,
  quickbooksNeedsMoreInfoQuestions,
  eachConnection,

  // Data helpers (return non-boolean, non-array values).
  quickbooksYesAnswerCount,

  // Runner.
  run,
};

export type { Data as CardRulesData, Predicate, ForEachSelector };
